/**
 * StudyPlanCard — "Prof. FreedrIQ recommends" personalized study plan.
 * Reads /api/freedriq/study-plan and lists the courses matching the learner's
 * weakest FreedrIQ categories. Used on the Dashboard and FreedrIQ result page.
 *
 * Props:
 *   compact — when true, show at most 3 recommendations without descriptions.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../App';
import { Sparkles, GraduationCap, ArrowRight, BookOpen, Loader2 } from 'lucide-react';

const StudyPlanCard = ({ compact = false }) => {
  const { API, user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/freedriq/study-plan?limit=${compact ? 3 : 5}`)
      .then((r) => setPlan(r.data))
      .catch(() => setPlan(null))
      .finally(() => setLoading(false));
  }, [API, user, compact]);

  if (loading) {
    return (
      <div className="pro-card p-4" data-testid="study-plan-loading">
        <div className="flex items-center gap-2 text-muted-foreground text-xs">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading study plan…
        </div>
      </div>
    );
  }

  if (!plan) return null;

  if (!plan.has_taken) {
    return (
      <div className="pro-card p-4 border-2 border-primary/40 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent" data-testid="study-plan-empty">
        <div className="eyebrow text-primary inline-flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Prof. FreedrIQ recommends
        </div>
        <div className="font-heading font-extrabold text-sm mt-1.5">Take the FreedrIQ first</div>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
          Once we know where you stand, we&apos;ll queue up the perfect first courses for you.
        </p>
        <Link
          to="/freedriq?tier=free&phase=pre"
          className="btn-primary inline-flex items-center gap-1.5 rounded-full font-bold text-xs px-3 py-1.5 mt-3"
          data-testid="study-plan-take-freedriq"
        >
          Take FreedrIQ <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    );
  }

  if ((plan.weakest_categories || []).length === 0) {
    return (
      <div className="pro-card p-4" data-testid="study-plan-topped-out">
        <div className="eyebrow text-emerald-700 inline-flex items-center gap-1.5">
          <GraduationCap className="w-3 h-3" /> Prof. FreedrIQ says
        </div>
        <div className="font-heading font-extrabold text-sm mt-1.5">
          You&apos;re scoring 80%+ everywhere
        </div>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
          No specific gaps to plug. Consider levelling up with the next-tier assessment.
        </p>
      </div>
    );
  }

  if ((plan.plan || []).length === 0) {
    return (
      <div className="pro-card p-4" data-testid="study-plan-no-match">
        <div className="eyebrow text-primary inline-flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Prof. FreedrIQ recommends
        </div>
        <div className="font-heading font-extrabold text-sm mt-1.5">
          Weakest areas: {plan.weakest_categories.slice(0, 3).join(', ')}
        </div>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
          No published courses match these topics yet — check back soon as new content is added.
        </p>
        <Link
          to="/learning?tab=courses"
          className="text-xs text-primary font-bold hover:underline mt-2 inline-flex items-center gap-1"
        >
          Browse all courses <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
    );
  }

  return (
    <div className="pro-card p-5" data-testid="study-plan-card">
      <div className="eyebrow text-primary inline-flex items-center gap-1.5">
        <Sparkles className="w-3 h-3" /> Prof. FreedrIQ recommends
      </div>
      <h3 className="font-heading font-extrabold text-base mt-1.5">Your personalised study plan</h3>
      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
        Based on your last FreedrIQ ({plan.latest_score}/100 · {plan.latest_tier}), these courses target your weakest categories.
      </p>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {plan.weakest_categories.slice(0, 3).map((cat) => (
          <span key={cat} className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary">
            {cat}
          </span>
        ))}
      </div>

      <div className="mt-4 space-y-2">
        {plan.plan.map((c) => (
          <Link
            key={c.course_id}
            to={`/learning/course/${c.course_id}`}
            className="block rounded-lg border border-border p-3 hover:border-primary/50 hover:bg-muted/50 transition-colors"
            data-testid={`study-plan-item-${c.course_id}`}
          >
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-extrabold text-sm truncate">{c.title || 'Untitled course'}</div>
                {!compact && c.description && (
                  <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2" dangerouslySetInnerHTML={{ __html: c.description }} />
                )}
                <div className="text-[10px] uppercase tracking-widest text-primary font-bold mt-1">
                  Targets: {c.matched_categories.slice(0, 2).join(' · ')}
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground mt-1" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default StudyPlanCard;
